import { Activity, FileText, MessageSquare, Mic, NotebookPen, Play, Users } from 'lucide-react';
import type {
    AiInterviewResult,
    CvScreeningResult,
    ReportKind,
    ReviewerScoreEdit,
} from '../types';
import { CVScreeningTab } from './tabs/CVScreeningTab';
import { AIInterviewTab } from './tabs/AIInterviewTab';
import { PanelInterviewTab } from './tabs/PanelInterviewTab';
import { TranscriptTab } from './tabs/TranscriptTab';
import { ActivityTab, type ActivityEvent } from './tabs/ActivityTab';
import { NotesTab } from './tabs/NotesTab';

export type RightTabKey =
    | 'cv_screening'
    | 'ai_interview'
    | 'panel'
    | 'transcript'
    | 'activity'
    | 'notes';

export interface ReportRightPanelProps {
    reportKind: ReportKind;
    cvScreening?: CvScreeningResult | null;
    aiInterview?: AiInterviewResult | null;
    activeTab: RightTabKey;
    onTabChange: (tab: RightTabKey) => void;
    activityEvents?: ActivityEvent[];
    applicationId: string;
    currentUserName: string;
    jobId?: string;
    taskId?: string;
    onEdited?: (edit: ReviewerScoreEdit) => void;
    /**
     * Scope clip — when present and non-empty, the tab list is filtered to
     * the listed kinds. Used by task-granted reviewers to hide tabs they
     * weren't invited to review.
     */
    scope?: { reportKinds: string[] };
}

const TAB_DEFS: Array<{
    key: RightTabKey;
    label: string;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}> = [
    { key: 'cv_screening', label: 'CV Screening', icon: FileText },
    { key: 'ai_interview', label: 'AI Interview', icon: Mic },
    { key: 'panel', label: 'Panel Interview', icon: Users },
    { key: 'transcript', label: 'Transcript', icon: Play },
    { key: 'activity', label: 'Activity', icon: Activity },
    { key: 'notes', label: 'Notes', icon: MessageSquare },
];

// Scope filter — given the reviewer's task scope, decide which tabs are visible.
// CV Screening and AI Interview tabs are kind-specific. Other tabs (transcript,
// activity, notes, panel) are always shown unless the scope includes only
// 'cv_screening' or 'ai_interview' alone — then secondary tabs hide too.
function filterTabsForScope(
    tabs: typeof TAB_DEFS,
    scope?: { reportKinds: string[] },
): typeof TAB_DEFS {
    if (!scope || !scope.reportKinds || scope.reportKinds.length === 0) return tabs;
    const kinds = new Set(scope.reportKinds);
    return tabs.filter((t) => {
        if (t.key === 'cv_screening') return kinds.has('cv_screening');
        if (t.key === 'ai_interview') return kinds.has('ai_interview');
        if (t.key === 'transcript') return kinds.has('ai_interview') || kinds.has('transcript');
        if (t.key === 'notes') return kinds.has('notes') || kinds.size > 0;
        if (t.key === 'panel') return false;
        if (t.key === 'activity') return kinds.size > 0;
        return true;
    });
}

export function ReportRightPanel({
    reportKind,
    cvScreening,
    aiInterview,
    activeTab,
    onTabChange,
    activityEvents,
    applicationId,
    currentUserName,
    jobId,
    taskId,
    onEdited,
    scope,
}: ReportRightPanelProps) {
    const overrides: ReviewerScoreEdit[] = [
        ...(cvScreening?.reviewerFeedback ?? []),
        ...(aiInterview?.reviewerFeedback ?? []),
    ];
    // Build componentId → label map so the Activity tab can render
    // "Engineering depth" instead of a raw mongoid.
    const componentLabels: Record<string, string> = {};
    cvScreening?.criteriaResult?.criteria.forEach((c) => { componentLabels[c.criterionId] = c.label; });
    cvScreening?.profileStrength?.components.forEach((c) => { componentLabels[c.componentId] = c.label; });
    aiInterview?.perRequirement?.forEach((r) => { componentLabels[r.criterionId] = r.label; });
    aiInterview?.perCriterion?.forEach((c) => { componentLabels[c.criterionId] = c.label; });
    const visibleTabs = filterTabsForScope(TAB_DEFS, scope);

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex flex-wrap gap-1.5">
                {visibleTabs.map((t) => {
                    const isActive = t.key === activeTab;
                    const headline =
                        (reportKind === 'cv_screening' && t.key === 'cv_screening') ||
                        (reportKind === 'ai_interview' && t.key === 'ai_interview');
                    const background = isActive
                        ? 'var(--surface-primary)'
                        : headline
                            ? 'var(--accent-subtle)'
                            : 'var(--surface-secondary)';
                    const color = isActive
                        ? 'var(--text-primary)'
                        : headline
                            ? 'var(--accent)'
                            : 'var(--text-tertiary)';
                    return (
                        <button
                            key={t.key}
                            onClick={() => onTabChange(t.key)}
                            className="flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12.5px] font-medium transition-all"
                            style={{
                                background,
                                color,
                                border: `1px solid ${
                                    isActive
                                        ? 'var(--border-emphasis)'
                                        : headline
                                            ? 'var(--border-accent)'
                                            : 'var(--border-default)'
                                }`,
                                boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                            }}
                            type="button"
                        >
                            <t.icon size={12} strokeWidth={1.75} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div
                className="flex-1 overflow-y-auto mt-3 rounded-xl"
                style={{
                    background: 'var(--surface-primary)',
                    border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-sm)',
                }}
            >
                {activeTab === 'cv_screening' &&
                    (cvScreening ? (
                        <CVScreeningTab
                            result={cvScreening}
                            jobId={jobId}
                            taskId={taskId}
                            onEdited={onEdited}
                        />
                    ) : (
                        <EmptyState icon={FileText} message="No CV screening report for this candidate yet." />
                    ))}

                {activeTab === 'ai_interview' &&
                    (aiInterview ? (
                        <AIInterviewTab
                            result={aiInterview}
                            jobId={jobId}
                            taskId={taskId}
                            onEdited={onEdited}
                        />
                    ) : (
                        <EmptyState icon={Mic} message="No AI interview snapshot for this candidate yet." />
                    ))}

                {activeTab === 'panel' && <PanelInterviewTab />}

                {activeTab === 'transcript' && (
                    <TranscriptTab transcript={aiInterview?.transcript ?? null} />
                )}

                {activeTab === 'activity' && (
                    <ActivityTab
                        events={activityEvents}
                        overrides={overrides}
                        componentLabels={componentLabels}
                    />
                )}

                {activeTab === 'notes' && (
                    <NotesTab applicationId={applicationId} currentUserName={currentUserName} />
                )}
            </div>
        </div>
    );
}

function EmptyState({
    icon: Icon,
    message,
}: {
    icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }>;
    message: string;
}) {
    return (
        <div className="px-5 py-12 text-center text-[14px]" style={{ color: 'var(--text-secondary)' }}>
            <Icon
                size={22}
                strokeWidth={1.5}
                style={{ display: 'inline', marginBottom: 10, color: 'var(--text-quaternary)' }}
            />
            <div>{message}</div>
        </div>
    );
}

// Keep NotebookPen import alive — referenced in case the parent wants to add a "Files" tab later.
void NotebookPen;
